import React, { useState, useEffect } from "react";
import Layout from "../components/Layout";

function Unsubscribe() {
  const [email, setEmail] = useState("");
  const [status, setStatus] = useState("loading"); // loading, success, error, not-found
  const [message, setMessage] = useState("");

  useEffect(() => {
    // Get email from URL parameters
    const urlParams = new URLSearchParams(window.location.search);
    const emailParam = urlParams.get("email");

    if (emailParam) {
      setEmail(decodeURIComponent(emailParam));
      // Auto-unsubscribe if email is in URL
      handleUnsubscribe(decodeURIComponent(emailParam));
    } else {
      setStatus("manual");
    }
  }, []);

  const handleUnsubscribe = async (emailToUnsubscribe = email) => {
    if (!emailToUnsubscribe || !emailToUnsubscribe.includes("@")) {
      setStatus("error");
      setMessage("Please enter a valid email address.");
      return;
    }

    setStatus("loading");

    try {
      const response = await fetch("/api/email/unsubscribe", {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          email: emailToUnsubscribe.trim(),
        }),
      });

      const result = await response.json();

      if (response.ok && result.success) {
        setStatus("success");
        setMessage(
          "You have been successfully unsubscribed from WebVenly marketing emails."
        );
      } else {
        setStatus("error");
        setMessage(
          result.error ||
            "There was an error processing your unsubscribe request."
        );
      }
    } catch (error) {
      setStatus("error");
      setMessage(
        "There was an error connecting to our servers. Please try again later."
      );
    }
  };

  const handleManualSubmit = (e) => {
    e.preventDefault();
    handleUnsubscribe();
  };

  return (
    <Layout>
      <div className="container py-5">
        <div className="row justify-content-center">
          <div className="col-lg-6">
            <div className="text-center mb-4">
              <h1 className="display-6 fw-bold mb-3">Email Unsubscribe</h1>
              <p className="lead text-muted">
                Manage your email preferences for WebVenly communications
              </p>
            </div>

            <div className="card shadow-sm border-0">
              <div className="card-body p-4">
                {status === "loading" && (
                  <div className="text-center py-4">
                    <div
                      className="spinner-border text-primary mb-3"
                      role="status"
                    >
                      <span className="visually-hidden">Loading...</span>
                    </div>
                    <p className="text-muted">
                      Processing your unsubscribe request...
                    </p>
                  </div>
                )}

                {status === "success" && (
                  <div className="text-center py-4">
                    <div className="mb-4">
                      <i
                        className="bi bi-check-circle-fill text-success"
                        style={{ fontSize: "4rem" }}
                      ></i>
                    </div>
                    <h4 className="text-success mb-3">
                      Successfully Unsubscribed
                    </h4>
                    <p className="text-muted mb-4">{message}</p>
                    <div className="bg-light rounded p-3 mb-4">
                      <h6 className="mb-2">What this means:</h6>
                      <ul className="text-start text-muted small mb-0">
                        <li>
                          You will no longer receive marketing emails from
                          WebVenly
                        </li>
                        <li>This change is effective immediately</li>
                        <li>
                          You may still receive transactional emails if you're a
                          customer
                        </li>
                        <li>
                          You can resubscribe at any time by contacting us
                        </li>
                      </ul>
                    </div>
                    <div className="d-flex flex-column flex-sm-row gap-2 justify-content-center">
                      <a href="/" className="btn btn-outline-primary">
                        <i className="bi bi-house me-2"></i>
                        Back to Website
                      </a>
                      <a href="/contact" className="btn btn-outline-secondary">
                        <i className="bi bi-envelope me-2"></i>
                        Contact Support
                      </a>
                    </div>
                  </div>
                )}

                {status === "error" && (
                  <div className="text-center py-4">
                    <div className="mb-4">
                      <i
                        className="bi bi-exclamation-triangle-fill text-warning"
                        style={{ fontSize: "4rem" }}
                      ></i>
                    </div>
                    <h4 className="text-warning mb-3">Unsubscribe Error</h4>
                    <p className="text-muted mb-4">{message}</p>
                    <button
                      className="btn btn-primary me-2"
                      onClick={() => setStatus("manual")}
                    >
                      Try Again
                    </button>
                    <a href="/contact" className="btn btn-outline-secondary">
                      Contact Support
                    </a>
                  </div>
                )}

                {status === "manual" && (
                  <div>
                    <div className="text-center mb-4">
                      <i
                        className="bi bi-envelope-x text-primary"
                        style={{ fontSize: "3rem" }}
                      ></i>
                    </div>

                    <h4 className="text-center mb-4">
                      Unsubscribe from Email List
                    </h4>

                    <form onSubmit={handleManualSubmit}>
                      <div className="mb-3">
                        <label htmlFor="email" className="form-label">
                          Email Address to Unsubscribe
                        </label>
                        <input
                          type="email"
                          className="form-control form-control-lg"
                          id="email"
                          value={email}
                          onChange={(e) => setEmail(e.target.value)}
                          placeholder="Enter your email address"
                          required
                        />
                        <div className="form-text">
                          Enter the email address you want to remove from our
                          mailing list
                        </div>
                      </div>

                      <div className="d-grid">
                        <button
                          type="submit"
                          className="btn btn-warning btn-lg"
                        >
                          <i className="bi bi-envelope-x me-2"></i>
                          Unsubscribe This Email
                        </button>
                      </div>
                    </form>

                    <div className="mt-4 p-3 bg-light rounded">
                      <h6 className="mb-2">Alternative Options:</h6>
                      <ul className="small text-muted mb-0">
                        <li>
                          Reply to any WebVenly email with "UNSUBSCRIBE" in the
                          subject
                        </li>
                        <li>
                          Contact us directly at{" "}
                          <a href="mailto:support@webvenly.com">
                            support@webvenly.com
                          </a>
                        </li>
                        <li>Call us at 681-363-0183 during business hours</li>
                      </ul>
                    </div>
                  </div>
                )}
              </div>
            </div>

            {/* Additional Information */}
            <div className="text-center mt-4">
              <div className="card border-info">
                <div className="card-body">
                  <h6 className="card-title text-info">
                    <i className="bi bi-info-circle me-2"></i>
                    Privacy Information
                  </h6>
                  <p className="card-text small text-muted mb-0">
                    We respect your privacy. Your email will be removed from our
                    marketing list within 24 hours. We do not share email
                    addresses with third parties.
                  </p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default Unsubscribe;
